/**
 * @file SendToSupplierFlow.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-21
 * @modified 2026-09-29
 * @description Sending a customer order's purchase orders to their suppliers,
 *              from wherever someone happens to be looking at that order.
 *
 *              THE AWKWARD CASE IS THE POINT. One order can be split across
 *              several suppliers, and each supplier gets its own purchase order
 *              with its own attachment, address and covering email. There is no
 *              single "send this order" — so rather than pick one PO and
 *              quietly send that, or open three modals in a row, this says so
 *              and offers the screen that can do them one at a time.
 *
 *              Search Purchase Orders is that screen, and it needs no new
 *              filter to get there: a PO raised from a customer order carries
 *              that order's number as its `reference`, which the PO search
 *              already matches on, so searching SO-D10146 lists exactly its
 *              purchase orders.
 *
 *              Four call sites use this — after creating an order, the row
 *              actions and right-click menu on Search Customer Orders, and the
 *              customer order modal — and they behave identically because the
 *              awkward case is decided here rather than four times over.
 */

import React, { useMemo, useState } from 'react';
import SendPOEmailModal from './SendPOEmailModal';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';

/**
 * How much of an order has reached its suppliers: 'unsent', 'partial' or
 * 'sent' — and null when no purchase order has been raised at all, which is a
 * different thing from one that has not been sent.
 *
 * Reads either spelling of the flag: the customer order list sends
 * `sent_to_contact` straight from the column, the purchase order search sends
 * `sentToContact` (see the API-shape note in CLAUDE.md).
 */
export function poSentState(linkedPos) {
  const pos = Array.isArray(linkedPos) ? linkedPos : [];
  if (pos.length === 0) return null;
  const sent = pos.filter(po => po.sent_to_contact ?? po.sentToContact ?? false).length;
  if (sent === 0) return 'unsent';
  if (sent === pos.length) return 'sent';
  return 'partial';
}

/** The class that paints the marker. Nothing when every PO has gone out. */
export function poSentRowClass(linkedPos) {
  const state = poSentState(linkedPos);
  if (state === 'unsent') return 'po-unsent';
  if (state === 'partial') return 'po-partly-sent';
  return '';
}

/** SendPOEmailModal wants an id and a number; linked_pos rows carry both. */
const asModalPo = (po) => ({
  purchaseOrderID: po.xero_purchase_order_id || po.purchaseOrderID || null,
  dbId: po.id ?? po.dbId ?? null,
  purchaseOrderNumber: po.purchase_order_number || po.purchaseOrderNumber || '',
  supplier_email: po.supplier_email || '',
  // A re-send's { summary, changes } — becomes the subject and message.
  revision: po.revision || null,
  // A cancellation's { reason } — the email says the PO is cancelled.
  cancellation: po.cancellation || null
});

/**
 * @param {array}    linkedPos   The order's purchase orders.
 * @param {string}   orderNumber For the wording, and for the PO search.
 * @param {object}   user        Passed to SendPOEmailModal for "send as".
 * @param {function} onClose     Always called when the flow finishes.
 * @param {function} onSent      (message) => void, after a successful send.
 * @param {function} onOpenInPOSearch (orderNumber) => void — the multi-supplier
 *                   way out. Omit it and that case just explains itself.
 */
function SendToSupplierFlow({ linkedPos, orderNumber, user, onClose, onSent, onOpenInPOSearch }) {
  const pos = useMemo(() => (Array.isArray(linkedPos) ? linkedPos : []), [linkedPos]);

  /*
   * Re-sends and cancellations, one after another. When every PO carries a
   * revision (an order edit or a moved date changed POs the suppliers already
   * hold) or a cancellation (they are being archived), each gets its own email
   * in turn — they are already chosen, and handing them over to a search screen
   * would lose what each email should say.
   */
  const sequential = pos.length > 1 && pos.every(po => po.revision || po.cancellation);
  const [step, setStep] = useState(0);
  const stepPo = useMemo(() => (sequential && pos[step] ? asModalPo(pos[step]) : null), [sequential, pos, step]);

  /*
   * Held still deliberately. SendPOEmailModal re-seeds itself when it is given a
   * different purchase order, and building this shape inline made it a
   * different one on every render — so every re-render of the screen underneath
   * threw away whatever had been typed into the email and re-fetched the PDF.
   * SendPOEmailModal no longer takes the bait, but there is no reason to hand it
   * a new object sixty times a minute either.
   */
  const singlePo = useMemo(() => (pos.length === 1 ? asModalPo(pos[0]) : null), [pos]);

  if (pos.length === 0) {
    return (
      <AlertDialog
        isOpen
        type="warning"
        message={`No purchase orders have been raised for ${orderNumber || 'this order'} yet, so there is nothing to send.`}
        onClose={onClose}
      />
    );
  }

  if (stepPo) {
    const next = () => (step + 1 < pos.length ? setStep(step + 1) : onClose?.());
    return (
      <SendPOEmailModal
        key={stepPo.purchaseOrderNumber}
        purchaseOrder={stepPo}
        user={user}
        onClose={next}
        onSent={(message) => { onSent?.(message, pos[step]); }}
      />
    );
  }

  if (singlePo) {
    return (
      <SendPOEmailModal
        purchaseOrder={singlePo}
        user={user}
        onClose={onClose}
        onSent={(message) => { onSent?.(message, pos[0]); onClose?.(); }}
      />
    );
  }

  // Several suppliers. Each needs its own email, so this hands over rather than
  // guessing which one was meant.
  const unsent = pos.filter(po => !(po.sent_to_contact ?? po.sentToContact ?? false));
  const numbers = pos
    .map(po => po.purchase_order_number || po.purchaseOrderNumber)
    .filter(Boolean)
    .join(', ');

  return (
    <ConfirmDialog
      isOpen
      type="info"
      title="More than one supplier"
      message={
        `${orderNumber || 'This order'} has ${pos.length} purchase orders (${numbers})` +
        `${unsent.length && unsent.length < pos.length ? `, ${unsent.length} of them not yet sent` : ''}` +
        `. Each supplier needs its own email, so they have to be sent one at a time. ` +
        `Open this order in Search Purchase Orders to send them?`
      }
      confirmText="Open in Search Purchase Orders"
      cancelText="Cancel"
      onConfirm={() => { onOpenInPOSearch?.(orderNumber); onClose?.(); }}
      onCancel={onClose}
    />
  );
}

export default SendToSupplierFlow;
